/**
 * "Who has what" — a read-only staff × section-group grid. Sticky first
 * column, horizontal scroll inside the card only (never the page), E (ruby) /
 * V (sky) / blank cells. Columns are grouped by section group rather than one
 * column per section (49 sections would be unreadable); hovering/tapping a
 * cell's title lists exactly which sections in that group are Edit vs View.
 */
import { ADMIN_SECTION_GROUPS, levelFor, type RolePermissions } from '@platform/shared-types';
import type { StaffMember, StaffRole } from '../../../services/staffApi';
import { sectionsInGroup, groupLevel } from './roleMath';

function cellFor(perms: RolePermissions, group: string): { text: string; cls: string; title: string } {
  const ids = sectionsInGroup(group);
  const level = groupLevel(perms, group);
  if (level === 'edit') return { text: 'E', cls: 'bg-[#DC143C] text-white', title: `Edit — all of ${group}` };
  if (level === 'view') return { text: 'V', cls: 'bg-sky-100 text-sky-800', title: `View — all of ${group}` };
  if (level === 'mixed') {
    const edit = ids.filter((id) => levelFor(perms, id) === 'edit').length;
    const view = ids.filter((id) => levelFor(perms, id) === 'view').length;
    return { text: '·', cls: 'bg-stone-100 text-stone-500', title: `Mixed — ${edit} Edit, ${view} View in ${group}` };
  }
  return { text: '', cls: '', title: `Off — none of ${group}` };
}

export function WhoHasWhat({ staff, roles }: { staff: StaffMember[]; roles: StaffRole[] }) {
  const roleById = new Map(roles.map((r) => [r.id, r]));
  const visible = staff.filter((s) => s.status !== 'terminated');

  return (
    <div className="bg-white text-stone-900 rounded-xl border border-stone-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border-collapse">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-200">
              <th className="sticky left-0 z-10 bg-stone-50 text-left px-4 py-2 font-semibold text-stone-700 min-w-[180px]">Staff</th>
              <th className="text-left px-3 py-2 font-semibold text-stone-700 min-w-[120px]">Role</th>
              {ADMIN_SECTION_GROUPS.map((g) => (
                <th key={g} className="px-2 py-2 font-medium text-stone-600 text-xs text-center whitespace-nowrap">{g}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((member) => {
              const role = roleById.get(member.roleId);
              const perms = role?.permissions ?? {};
              return (
                <tr key={member.id} className="border-b border-stone-100">
                  <td className="sticky left-0 z-10 bg-white px-4 py-2 font-medium text-stone-900 whitespace-nowrap">{member.name}</td>
                  <td className="px-3 py-2 text-stone-600 whitespace-nowrap">{role?.name ?? '—'}</td>
                  {ADMIN_SECTION_GROUPS.map((g) => {
                    const cell = cellFor(perms, g);
                    return (
                      <td key={g} className="px-2 py-2 text-center">
                        {cell.text && (
                          <span title={cell.title} className={`inline-flex w-6 h-6 items-center justify-center rounded text-[11px] font-semibold ${cell.cls}`}>
                            {cell.text}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr>
                <td colSpan={2 + ADMIN_SECTION_GROUPS.length} className="px-4 py-6 text-center text-stone-500">
                  No active staff yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default WhoHasWhat;
